// Service worker registration (web only), plus the state behind the update toast and the
// offline/online banner. The worker is sw/service-worker.js; the messages below are its protocol.

export const swState = $state({
  /** A new version is installed and waiting; the update toast is shown. */
  updateAvailable: false,
  /** The connectivity banner: `active` restarts its slide-in-and-out animation. */
  connectivity: { text: "", active: false },
});

const showConnectivity = (text: string): void => {
  swState.connectivity.text = text;
  if (swState.connectivity.active) {
    // Replay the animation: drop the class, then add it back.
    swState.connectivity.active = false;
    setTimeout(() => {
      swState.connectivity.active = true;
    }, 1000);
  } else {
    swState.connectivity.active = true;
  }
};

const detectOffline = (): void =>
  showConnectivity("Offline, requests are all served through cache");
const detectOnline = (): void => showConnectivity("Back Online!");

const listenForBannerMessages = (): void => {
  navigator.serviceWorker.addEventListener("message", (event: MessageEvent<{ text?: string }>) => {
    switch (event.data?.text) {
      case "CLOSE_BANNER":
        swState.updateAvailable = false;
        break;
      case "OPEN_BANNER":
        // Really, this will never get called.
        swState.updateAvailable = true;
        break;
    }
  });
};

const registerValidSW = (): void => {
  navigator.serviceWorker
    .register("/service-worker.js")
    .then((registration) => {
      registration.onupdatefound = (): void => {
        const installingWorker = registration.installing;
        if (installingWorker) {
          installingWorker.onstatechange = (): void => {
            switch (installingWorker.state) {
              case "installed":
                if (navigator.serviceWorker.controller) {
                  swState.updateAvailable = true;
                } else {
                  console.warn("No current controller, sw is initialized for the first time");
                }
                break;
              case "redundant":
                console.warn("The installing service worker became redundant");
                break;
            }
          };
        }
      };
    })
    .catch((error) => {
      console.error(`Service Worker Registration Failed with ${String(error)}`);
    });
  navigator.serviceWorker.onmessage = (event): void => {
    /**
     * Provide a mechanism for the service worker to request that the main thread reload's the current user.
     * The primary use case is for the service worker to force the web app to use the latest assets after the user
     * has opted to update the app.
     */
    if (event.data === "REFRESH_WINDOW") {
      window.location.reload();
    }
  };
};

export const registerServiceWorker = (): void => {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  if (navigator.serviceWorker.controller) {
    // An update that was skipped earlier is applied on the next page load.
    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (registration?.waiting?.state === "installed") {
          registration.waiting.postMessage("SKIP_WAITING");
        }
      })
      .catch((err) => {
        console.error("Could not register service worker!");
        console.error(err);
      });
  } else {
    console.warn("This page is not currently controlled by a service worker");
  }

  // Register once the page has loaded, so the first visit isn't slowed down. SvelteKit may start the
  // app after the load event has already fired.
  const onLoad = (): void => {
    listenForBannerMessages();
    registerValidSW();
  };
  if (document.readyState === "complete") {
    onLoad();
  } else {
    window.addEventListener("load", onLoad, { once: true });
  }
  window.addEventListener("offline", detectOffline);
  window.addEventListener("online", detectOnline);
};

/** Native apps load their assets from the filesystem: drop any worker and caches left from before. */
export const unregisterServiceWorker = (): void => {
  if (!("serviceWorker" in navigator)) {
    return;
  }
  caches
    .keys()
    .then((cacheNames) => {
      cacheNames.forEach((cacheName) => {
        caches.delete(cacheName).catch((err) => {
          console.error(`Could not delete cache ${cacheName}`);
          console.error(err);
        });
      });
    })
    .catch((err) => {
      console.error("Could not unregister service worker");
      console.error(err);
    });

  navigator.serviceWorker.ready
    .then(async (registration) => {
      await registration.unregister();
    })
    .catch((error: Error) => {
      console.error(error.message);
    });
};

const postToWaitingWorker = async (message: "SKIP_WAITING" | "NO_SKIP_WAITING"): Promise<void> => {
  const registration = await navigator.serviceWorker.getRegistration();
  registration?.waiting?.postMessage(message);
};

/** The worker activates, then asks every window to reload (REFRESH_WINDOW). */
export const applyUpdate = (): Promise<void> => postToWaitingWorker("SKIP_WAITING");

/** The worker closes the toast (CLOSE_BANNER) and activates on its own a little later. */
export const skipUpdate = (): Promise<void> => postToWaitingWorker("NO_SKIP_WAITING");
